import React, {useCallback, useEffect, useState, useMemo} from 'react';

import PropTypes from 'prop-types';

import ApiFetcher3 from '../../src/modules/apis/ApiFetcher3';

const withV3AssignmentApiGet = (ComponentToWrap) => {
  const propTypes = {
    id: PropTypes.string,
    lazy: PropTypes.bool,
    params: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
  };
  const WrappedWithV3AssignmentsApiGet = ({
    id,
    lazy,
    params,
    ...otherProps
  }) => {
    // Calculating the params string helps make sure the API loading effect
    // below only re-runs if the actual param string has changed, even if the
    // input params prop references a different object.
    const paramsStr = useMemo(() => {
      // Compute the search params as a string.
      if (!params) return 'use_imgix=true';
      if (typeof params === 'string') return `${params}&use_imgix=true`;
      return new URLSearchParams({use_imgix: true, ...params}).toString();
    }, [params]);

    // Define the assignments API call state, and load it.
    const [assignmentsApiCallState, setAssignmentsApiCallState] = useState({isLoading: undefined});
    const retrieveAssignmentsApiCallFunc = useCallback(() => {
      let canceled = false;
      const load = async() => {
        // Set the state to loading, unless it already is.
        setAssignmentsApiCallState((current) => {
          if (current.isLoading) return current;
          return {...current, isLoading: true};
        });
        const idPathPortion = id ? `${id}/` : '';
        try {
          // Make the API call.
          const {data} = await ApiFetcher3.get(`assignment/${idPathPortion}`, paramsStr);
          if (canceled) return;
          setAssignmentsApiCallState({data, isLoading: false});
        } catch (error) {
          if (canceled) return;
          setAssignmentsApiCallState({error, isLoading: false});
        }
      };
      load();
      return () => {
        // Cancel state change, in case the component unmounts, for example.
        canceled = true;
      };
    }, [id, paramsStr]);
    useEffect(() => {
      if (!lazy) {
        retrieveAssignmentsApiCallFunc();
      }
    }, [id, paramsStr]);

    return (
      <ComponentToWrap
        assignmentsApiCallState={assignmentsApiCallState}
        retrieveAssignmentsApiCallFunc={retrieveAssignmentsApiCallFunc}
        {...otherProps}
      />
    );
  };

  WrappedWithV3AssignmentsApiGet.propTypes = propTypes;

  return WrappedWithV3AssignmentsApiGet;
};

export default withV3AssignmentApiGet;
